import type { EquipmentInstance, EquipmentTemplate } from '../equipment/equipmentTypes';
import { getElementPorts } from '../layout/layoutSlice';
import type {
  AmhsDedicatedPathStep,
  AmhsLoadPort,
  AmhsOht,
  AmhsRunRequest,
  AmhsStocker,
  AmhsSwitch,
  AmhsSwitchThrow,
  AmhsTool,
} from '../../types/amhsSimulationRequest';
import type { LayoutElement, TrackNodeAnnotation } from '../../types';
import {
  getConnectorShiftOffsetMeters,
  getConnectorShiftState,
  isConnectorElement,
} from '../../utils/connectorRail';
import { portWorldPosition, resolvePorts, resolveTemplate } from '../../utils/equipmentGeometry';
import { isDxfEquipmentLayoutElement } from '../../utils/layout3d';
//import { type Port, makeDefaultPorts, alignEquipmentPortsToLoadDocks, portLocalPosition, resolvePortKind, stockerRackRole } from '../../utils/ports';
import {
  type Port,
  makeDefaultPorts,
  alignEquipmentPortsToLoadDocks,
  portLocalPosition,
  resolveBayType,
  resolveColumnKind,
  resolveInventoryLevel,
  resolvePortKind,
} from '../../utils/ports';
import {
  buildTrackGraph,
  findNearestNodeOnTrack,
  type TrackGraph,
} from '../../utils/simulationGraph';
import {
  getExportableTrackEdges,
  simulationTrackNodeId,
} from '../../utils/simulationTrackNetworkView';
import {
  DEFAULT_GATE_FOUP_CAPACITY,
  resolveTrackNodeLandmarks,
  SYNTHETIC_ENTRY_LOAD_PORT_ID,
  SYNTHETIC_EXIT_LOAD_PORT_ID,
} from '../../utils/trackNodeAnnotations';
import { ENDPOINT_TOLERANCE, isJunctionRailType, resolveRailType } from '../../utils/topology';
import { getJunctionWaitSec } from '../../utils/junctionTurntableSim';
import { resolveStockerSlotCapacity } from '../../utils/stockerCapacity';
import {
  AMHS_PHYSICS_DEFAULTS,
  AMHS_RUN_INVENTORY,
  AMHS_SIMULATION_CONFIG,
  DEFAULT_DROP_TO_PICK_SEC,
  buildDefaultFoups,
} from './amhsSimulationConfig';
import {
  buildTransportFlowFromLayout,
  buildProcessToolIndexByToolId,
  stockerIdForToolType,
} from './buildTransportFlow';
import {
  buildDedicatedPath,
  deriveOhtRoleFromEndpoints,
  deriveToolTypeFromEndpoints,
  parseDedicatedEndpoint,
  parseOhtRole,
  semanticDedicatedEndpoint,
} from './ohtDedicatedEndpoints';
import {
  DEFAULT_PROCESS_WAIT_SEC,
  DEFAULT_STOCKER_PROCESS_WAIT_SEC,
  normalizeEquipmentProcessType,
} from '../equipment/equipmentConstants';
import { primaryProcessType, readStockerProcessTypes } from '../equipment/stockerProcessTypes';
import {
  buildAmhsProcessSequence,
  readProcessFlow,
  resolveBayPathToolIds,
  type ProcessFlowStep,
} from './processFlow';
import { resolveOuterLoopStockerIds } from './outerLoopStockers';

export type BuildAmhsRunRequestInput = {
  elements: Record<string, LayoutElement>;
  equipmentInstances: Record<string, EquipmentInstance>;
  equipmentTemplates: Record<string, EquipmentTemplate>;
  floorPlanId?: string | null;
  floorPlanName?: string | null;
  trackNodeAnnotations?: TrackNodeAnnotation[];
  /** Layout JSON project metadata (inventory / transportFlow). */
  projectMetadata?: Record<string, unknown> | null;
  /**
   * Mandatory stop, in wall seconds, applied to every exported junction.
   * Shared across all junctions; `0` disables the dwell.
   * The live websocket payload sends `waitSec × simSpeed`.
   */
  junctionWaitSec?: number;
  /**
   * When false, omit `transportFlow` from the `/amhs/run` payload and set
   * `includeTransportFlow: false`. Defaults to true.
   */
  includeTransportFlow?: boolean;
};

/** Junction dwell used when neither the request nor the layout specifies one. */
const DEFAULT_JUNCTION_REQUEST_WAIT_SEC = 3;

type Point = { x: number; y: number };

type StockerSource =
  | { kind: 'layout'; element: LayoutElement }
  | { kind: 'equipment'; instance: EquipmentInstance; template: EquipmentTemplate | null };

type ToolSource =
  | { kind: 'equipment'; instance: EquipmentInstance; template: EquipmentTemplate | null }
  | { kind: 'layout'; element: LayoutElement };

/** Layout floor tools that render as process equipment in 3D but live outside the Equipment Library. */
function isLayoutToolElement(element: LayoutElement): boolean {
  if (!element.metadata?.visible) return false;
  if (element.type === 'stocker' || element.type === 'oht-vehicle') return false;
  if (isDxfEquipmentLayoutElement(element)) return true;
  // Toolbox AMHS floor units (always process/load-port interfaces in kit mode).
  return element.type === 'ohb' || element.type === 'lifter' || element.type === 'stb';
}

/** Equipment-library overlays that act as process tools (not stockers/rooms). */
function isToolEquipmentInstance(instance: EquipmentInstance): boolean {
  return instance.objectType !== 'stocker' && instance.objectType !== 'room';
}

function toolSourceId(source: ToolSource): string {
  return source.kind === 'layout' ? source.element.id : source.instance.id;
}

function toolSourceName(source: ToolSource): string {
  return source.kind === 'layout' ? source.element.metadata.name : source.instance.name;
}

function toolSourceType(source: ToolSource): string {
  if (source.kind === 'equipment') {
    return normalizeEquipmentProcessType(source.instance.equipmentType);
  }
  const props = source.element.customProperties || {};
  return normalizeEquipmentProcessType(
    props.processType ?? props.equipmentType ?? source.element.metadata.name
  );
}

function toolSourceProcessWaitSec(source: ToolSource): number {
  if (source.kind === 'equipment') {
    return resolveProcessWaitSec(source.instance.processWaitSec);
  }
  return resolveProcessWaitSec(source.element.customProperties?.processWaitSec);
}

function toolSourceFoupCapacity(source: ToolSource): number {
  const configured =
    source.kind === 'equipment'
      ? source.instance.foupCapacity
      : source.element.customProperties?.foupCapacity;
  const toolPorts = getToolPorts(source);
  const loadCount = toolPorts.filter(({ port }) => equipmentPortKind(port) === 'LOAD').length;
  const unloadCount = toolPorts.filter(({ port }) => equipmentPortKind(port) === 'UNLOAD').length;
  const pairCount = Math.min(loadCount, unloadCount);
  return Math.max(
    1,
    Math.floor(
      readPositiveCapacity(configured) ?? (pairCount > 0 ? pairCount : TOOL_SHARED_FOUP_CAPACITY)
    )
  );
}

function getToolPorts(source: ToolSource): Array<{ port: Port; position: Point }> {
  if (source.kind === 'equipment') {
    return resolvePorts(source.instance, source.template).map((port) => ({
      port,
      position: portWorldPosition(port, source.instance),
    }));
  }
  let ports = getElementPorts(source.element);
  if (ports.length === 0) {
    ports = makeDefaultPorts({
      parentId: source.element.id,
      parentType: 'equipment',
      width: source.element.dimensions?.width,
      height: source.element.dimensions?.height,
    });
  } else {
    ports = alignEquipmentPortsToLoadDocks(
      ports,
      source.element.dimensions?.width ?? 0,
      source.element.dimensions?.height ?? 0
    );
  }
  return ports.map((port) => ({
    port,
    position: layoutPortPosition(source.element, port),
  }));
}

export class AmhsRequestValidationError extends Error {
  constructor(public readonly issues: string[]) {
    super(`Cannot run simulation: ${issues.join('; ')}.`);
    this.name = 'AmhsRequestValidationError';
  }
}

function finitePositive(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function layoutPoint(element: LayoutElement, local: Point): Point {
  const radians = (element.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: element.position.x + local.x * cos - local.y * sin,
    y: element.position.y + local.x * sin + local.y * cos,
  };
}

function layoutPortPosition(element: LayoutElement, port: Port): Point {
  return layoutPoint(
    element,
    portLocalPosition(port, {
      x: 0,
      y: 0,
      w: element.dimensions.width,
      h: element.dimensions.height,
    })
  );
}

function elementAnchor(element: LayoutElement): Point {
  const pivotX = Number(element.customProperties?.pivotX ?? 0);
  const pivotY = Number(element.customProperties?.pivotY ?? 0);
  return layoutPoint(element, {
    x: Number.isFinite(pivotX) ? pivotX : 0,
    y: Number.isFinite(pivotY) ? pivotY : 0,
  });
}

function nearestNodeId(graph: TrackGraph, point: Point): string | null {
  const index = findNearestNodeOnTrack(graph, point);
  return index === null ? null : simulationTrackNodeId(index);
}

function portsCentroid(ports: ReadonlyArray<{ position: Point }>): Point | null {
  if (ports.length === 0) return null;
  let x = 0;
  let y = 0;
  for (const { position } of ports) {
    x += position.x;
    y += position.y;
  }
  return { x: x / ports.length, y: y / ports.length };
}

/**
 * Rail node every dock on one tool or stocker reports. The reference is the
 * centroid of all port world positions (e.g. LP-1…LP-4 → between LP-2 and
 * LP-3), then the nearest on-track node to that midpoint.
 */
function resolveSharedTrackNodeId(
  graph: TrackGraph,
  ports: ReadonlyArray<{ port: Port; position: Point }>
): string | null {
  const midpoint = portsCentroid(ports);
  return midpoint ? nearestNodeId(graph, midpoint) : null;
}

/** Per-dock FOUP hold on a tool; shared pool is {@link TOOL_SHARED_FOUP_CAPACITY}. */
const TOOL_PORT_FOUP_CAPACITY = 1;
/** Shared FOUP hold across all docks on one tool (`sharedGroupId` = toolId). */
const TOOL_SHARED_FOUP_CAPACITY = 2;

/** Split stocker total capacity into Raw + Processed columns (sample: 50/50). */
function splitStockerColumnCapacities(foupCapacity: number): {
  rawFoupCapacity: number;
  processedFoupCapacity: number;
} {
  const total = Math.max(0, Math.floor(foupCapacity));
  const rawFoupCapacity = Math.floor(total / 2);
  return {
    rawFoupCapacity,
    processedFoupCapacity: total - rawFoupCapacity,
  };
}

function buildOuterLoopPath(stockerIds: readonly string[]): AmhsDedicatedPathStep[] {
  const path: AmhsDedicatedPathStep[] = [{ ENTRY: 'Entry', portKind: 'ENTRY_GATE' }];
  for (const stockerIdValue of stockerIds) {
    path.push(
      { STOCKER: stockerIdValue, portKind: 'Interbay' },
      { wait: 'inventory', at: stockerIdValue, portKind: 'Intrabay' }
    );
  }
  path.push({ EXIT: 'Exit', portKind: 'EXIT_GATE' });
  return path;
}

function buildBayPath(
  stockerIdValue: string,
  processType: string,
  tools: readonly AmhsTool[],
  processStep?: ProcessFlowStep
): AmhsDedicatedPathStep[] | undefined {
  if (!stockerIdValue || !processType) return undefined;
  const matchingToolIds = resolveBayPathToolIds(processStep, tools, processType);
  return [
    { STOCKER: stockerIdValue, portKind: 'Intrabay' },
    ...matchingToolIds.map((toolId) => ({ TOOL: toolId, portKind: 'Intrabay' })),
    { STOCKER: stockerIdValue, portKind: 'Intrabay' },
  ];
}

/** Rewrite STOCKER hops (and wait `at`) from process type → stockerId. */
function mapDedicatedPathToStockerIds(
  path: AmhsDedicatedPathStep[] | undefined,
  stockerLookup: ReadonlyArray<Pick<AmhsStocker, 'stockerId' | 'name' | 'processType'>>,
  fallbackStockerId: string
): AmhsDedicatedPathStep[] | undefined {
  if (!path) return undefined;
  return path.map((step) => {
    const next = { ...step };
    if (typeof next.STOCKER === 'string' && next.STOCKER) {
      next.STOCKER = stockerIdForToolType(stockerLookup, next.STOCKER, fallbackStockerId);
    }
    if (typeof next.at === 'string' && next.at) {
      next.at = stockerIdForToolType(stockerLookup, next.at, fallbackStockerId);
    }
    return next;
  });
}

function equipmentPortKind(port: Port): AmhsLoadPort['portKind'] {
  return resolvePortKind(port, 'equipment');
}

function bayPortKind(port: Port, parentType: 'equipment' | 'stocker'): 'Intrabay' | 'Interbay' {
  return resolveBayType(port, parentType) === 'interbay' ? 'Interbay' : 'Intrabay';
}

function getStockerPorts(source: StockerSource): Array<{ port: Port; position: Point }> {
  if (source.kind === 'layout') {
    return getElementPorts(source.element).map((port) => ({
      port,
      position: layoutPortPosition(source.element, port),
    }));
  }
  return resolvePorts(source.instance, source.template).map((port) => ({
    port,
    position: portWorldPosition(port, source.instance),
  }));
}

function stockerId(source: StockerSource): string {
  return source.kind === 'layout' ? source.element.id : source.instance.id;
}

function stockerName(source: StockerSource): string {
  return source.kind === 'layout' ? source.element.metadata.name : source.instance.name;
}

function readPositiveCapacity(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Resolve request `inventory.foupCapacity` from layout metadata (Simulation panel). */
export function resolveInventoryFoupCapacity(
  projectMetadata?: Record<string, unknown> | null
): number | undefined {
  const inventory =
    projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
      ? (projectMetadata.inventory as Record<string, unknown>)
      : {};
  return readPositiveCapacity(inventory.foupCapacity);
}

function stockerCapacity(source: StockerSource, inventoryDefault?: number): number {
  const bayCount =
    source.kind === 'layout'
      ? source.element.customProperties?.bayCount
      : (source.instance as EquipmentInstance & { bayCount?: string | number }).bayCount;
  const parsedBayCount = Math.floor(Number(bayCount));
  // The physical rack configuration is authoritative when present: 4/6/8 bays
  // correspond to 28/42/56 slots in both the canvas and Review configuration.
  const fromBayConfiguration =
    parsedBayCount === 4 || parsedBayCount === 6 || parsedBayCount === 8
      ? resolveStockerSlotCapacity(parsedBayCount)
      : undefined;
  const fromEntity =
    source.kind === 'layout'
      ? readPositiveCapacity(source.element.customProperties?.foupCapacity)
      : readPositiveCapacity(source.instance.foupCapacity);
  return fromBayConfiguration ?? fromEntity ?? inventoryDefault ?? 100;
}

function isOutsideStocker(source: StockerSource): boolean {
  return source.kind === 'layout'
    ? source.element.customProperties?.outsideStocker === true
    : source.instance.outsideStocker === true;
}

function resolveProcessWaitSec(value: unknown): number {
  const parsed = Number(value);
  // Allow 0 (no dwell). Only fall back when missing / non-numeric / negative.
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return DEFAULT_PROCESS_WAIT_SEC;
}

function stockerProcessWaitSec(source: StockerSource): number {
  const value =
    source.kind === 'layout'
      ? source.element.customProperties?.processWaitSec
      : source.instance.processWaitSec;
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return DEFAULT_STOCKER_PROCESS_WAIT_SEC;
}

function stockerRequireInventoryBeforePick(source: StockerSource): boolean {
  if (source.kind === 'layout') {
    return source.element.customProperties?.requireInventoryBeforePick !== false;
  }
  return (
    (source.instance as { requireInventoryBeforePick?: boolean }).requireInventoryBeforePick !==
    false
  );
}

/**
 * Process type only when the layout/instance sets one.
 * `processTypes: []` / `null` (UI "None") and missing fields → omit (no PHOTO default).
 */
function stockerExplicitProcessTypes(source: StockerSource) {
  return source.kind === 'layout'
    ? readStockerProcessTypes({
        processTypes: source.element.customProperties?.processTypes,
        legacyProcessType:
          source.element.customProperties?.processType ?? source.element.customProperties?.toolType,
      })
    : readStockerProcessTypes({
        processTypes: source.instance.processTypes,
        legacyProcessType:
          source.instance.equipmentType ??
          (source.instance as { toolType?: unknown }).toolType ??
          source.instance.metadata?.toolType,
      });
}

function stockerExplicitProcessType(source: StockerSource): string {
  return primaryProcessType(stockerExplicitProcessTypes(source)) ?? '';
}

/** Resolve request `inventory.enabled` from layout metadata (default false). */
export function resolveInventoryEnabled(projectMetadata?: Record<string, unknown> | null): boolean {
  const inventory =
    projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
      ? (projectMetadata.inventory as Record<string, unknown>)
      : {};
  const flow =
    projectMetadata &&
    typeof projectMetadata.transportFlow === 'object' &&
    projectMetadata.transportFlow
      ? (projectMetadata.transportFlow as Record<string, unknown>)
      : {};

  if (typeof inventory.enabled === 'boolean') return inventory.enabled;
  if (typeof inventory.requireInventoryBeforeProcess === 'boolean') {
    return inventory.requireInventoryBeforeProcess;
  }
  if (typeof flow.inventoryEnabled === 'boolean') return flow.inventoryEnabled;
  return AMHS_SIMULATION_CONFIG.inventory?.enabled === true;
}

export function buildAmhsRunRequest(input: BuildAmhsRunRequestInput): AmhsRunRequest {
  const layoutElements = Object.values(input.elements);
  const graph = buildTrackGraph(layoutElements);
  const equipmentInstances = Object.values(input.equipmentInstances);
  const inventoryEnabled = resolveInventoryEnabled(input.projectMetadata);
  const inventoryFoupCapacity = resolveInventoryFoupCapacity(input.projectMetadata);
  const includeTransportFlow = input.includeTransportFlow !== false;

  const equipmentStockers: StockerSource[] = equipmentInstances
    .filter((instance) => instance.objectType === 'stocker')
    .map((instance) => ({
      kind: 'equipment',
      instance,
      template: resolveTemplate(instance, input.equipmentTemplates),
    }));
  const equipmentStockerIds = new Set(equipmentStockers.map(stockerId));
  const layoutStockers: StockerSource[] = layoutElements
    .filter(
      (element) =>
        element.type === 'stocker' &&
        element.metadata.visible &&
        !equipmentStockerIds.has(element.id)
    )
    .map((element) => ({ kind: 'layout', element }));
  const stockerSources = [...equipmentStockers, ...layoutStockers];

  const equipmentToolSources: ToolSource[] = equipmentInstances
    .filter(isToolEquipmentInstance)
    .map((instance) => ({
      kind: 'equipment' as const,
      instance,
      template: resolveTemplate(instance, input.equipmentTemplates),
    }));
  const equipmentToolIds = new Set(equipmentToolSources.map(toolSourceId));
  // Also accept DXF/layout process tools that render in 3D but are not Equipment Library instances.
  const layoutToolSources: ToolSource[] = layoutElements
    .filter(isLayoutToolElement)
    .filter((element) => {
      const dxfId = element.customProperties?.dxfEquipmentId;
      if (typeof dxfId === 'string' && equipmentToolIds.has(dxfId)) return false;
      return !equipmentToolIds.has(element.id);
    })
    .map((element) => ({ kind: 'layout' as const, element }));
  const toolSources: ToolSource[] = [...equipmentToolSources, ...layoutToolSources];

  const tools: AmhsTool[] = toolSources.map((source) => ({
    toolId: toolSourceId(source),
    name: toolSourceName(source),
    toolType: toolSourceType(source),
    processFamily: toolSourceType(source),
    status: 'Available' as const,
    processWaitSec: toolSourceProcessWaitSec(source),
    foupCapacity: toolSourceFoupCapacity(source),
  }));
  const processToolIndexById = buildProcessToolIndexByToolId(tools);

  const stockers: AmhsStocker[] = stockerSources.map((source) => {
    const outside = isOutsideStocker(source);
    const processType = stockerExplicitProcessType(source);
    // Payload rule: processType present → process; absent/None → passThrough.
    const includeProcessType = !outside && Boolean(processType);
    const foupCapacity = stockerCapacity(source, inventoryFoupCapacity);
    const columns = splitStockerColumnCapacities(foupCapacity);
    return {
      stockerId: stockerId(source),
      name: stockerName(source),
      status: 'Available' as const,
      foupCapacity,
      ...columns,
      outsideStocker: outside,
      processWaitSec: stockerProcessWaitSec(source),
      requireInventoryBeforePick: inventoryEnabled && stockerRequireInventoryBeforePick(source),
      ...(includeProcessType ? { processType } : {}),
      materialFlow: includeProcessType ? ('process' as const) : ('passThrough' as const),
    };
  });
  const stockerCapacityById = new Map(
    stockers.map((stocker) => [stocker.stockerId, stocker.foupCapacity])
  );
  // Include processType even for outside stockers so dedicated paths can resolve
  // e.g. PHOTO → that stocker's id (outside stockers omit processType in the payload).
  // Only use an explicit layout/instance type — do not invent DEFAULT PHOTO for untyped stockers.
  const stockerLookup = stockerSources.map((source) => ({
    stockerId: stockerId(source),
    name: stockerName(source),
    processType: stockerExplicitProcessType(source) || undefined,
  }));
  const fallbackStockerId =
    stockers.find((s) => s.outsideStocker)?.stockerId ?? stockers[0]?.stockerId ?? '';

  const loadPorts: AmhsLoadPort[] = [];
  let canvasLoadPortCount = 0;
  for (const source of toolSources) {
    const toolPorts = getToolPorts(source);
    const toolId = toolSourceId(source);
    const toolType = toolSourceType(source);
    const processToolIndex = processToolIndexById.get(toolId) ?? 1;
    const loadPortsForTool = toolPorts.filter(({ port }) => equipmentPortKind(port) === 'LOAD');
    const unloadPortsForTool = toolPorts.filter(({ port }) => equipmentPortKind(port) === 'UNLOAD');
    const processDestinationByLoadPortId =
      toolPorts.length > 2
        ? new Map(
            loadPortsForTool.flatMap(({ port }, index) => {
              const unloadPort = unloadPortsForTool[index]?.port;
              return unloadPort ? [[port.id, unloadPort.id] as const] : [];
            })
          )
        : new Map<string, string>();
    // All docks on a tool share one rail attachment node (sample contract).
    const sharedTrackNodeId = resolveSharedTrackNodeId(graph, toolPorts);
    for (const { port, position } of toolPorts) {
      canvasLoadPortCount += 1;
      const trackNodeId = sharedTrackNodeId ?? nearestNodeId(graph, position);
      if (!trackNodeId) continue;
      const functionalKind = equipmentPortKind(port);
      const processToPortId = processDestinationByLoadPortId.get(port.id);
      const portKind = includeTransportFlow ? functionalKind : bayPortKind(port, 'equipment');
      loadPorts.push({
        loadPortId: port.id,
        toolId,
        toolType,
        trackNodeId,
        portKind,
        bayType: resolveBayType(port, 'equipment'),
        status: 'Available',
        processToolIndex,
        foupCapacity: TOOL_PORT_FOUP_CAPACITY,
        ...(processToPortId ? { processToPortId } : {}),
      });
    }
  }
  for (const source of stockerSources) {
    const sid = stockerId(source);
    const capacity = stockerCapacityById.get(sid) ?? 100;
    const stockerPortEntries = getStockerPorts(source);
    const sharedTrackNodeId = resolveSharedTrackNodeId(graph, stockerPortEntries);
    const pending: Array<{
      loadPortId: string;
      bayType: 'interbay' | 'intrabay';
      columnKind: 'raw' | 'processed';
      inventoryLevel: 'stocker' | 'bay';
      trackNodeId: string;
    }> = [];

    for (const { port, position } of stockerPortEntries) {
      canvasLoadPortCount += 1;
      const trackNodeId = sharedTrackNodeId ?? nearestNodeId(graph, position);
      if (!trackNodeId) continue;
      const bayType = resolveBayType(port, 'stocker');
      const columnKind = resolveColumnKind(port, 'stocker') ?? 'raw';
      const inventoryLevel =
        resolveInventoryLevel(port, 'stocker') ?? (columnKind === 'processed' ? 'bay' : 'stocker');
      pending.push({
        loadPortId: port.id,
        bayType,
        columnKind,
        inventoryLevel,
        trackNodeId,
      });
    }

    for (const entry of pending) {
      const isDepositFace =
        (entry.columnKind === 'raw' && entry.bayType === 'interbay') ||
        (entry.columnKind === 'processed' && entry.bayType === 'intrabay');
      const linked = isDepositFace
        ? pending.find(
            (other) =>
              other.loadPortId !== entry.loadPortId &&
              other.columnKind === entry.columnKind &&
              other.bayType !== entry.bayType
          )
        : undefined;
      loadPorts.push({
        loadPortId: entry.loadPortId,
        stockerId: sid,
        trackNodeId: entry.trackNodeId,
        // Sample contract: portKind is the bay face; column is tagged separately.
        portKind: entry.bayType === 'interbay' ? 'Interbay' : 'Intrabay',
        status: 'Available',
        foupCapacity: capacity,
        sharedGroupId: sid,
        sharedCapacity: capacity,
        ...(linked ? { processToPortId: linked.loadPortId } : {}),
        columnKind: entry.columnKind,
        inventoryLevel: entry.inventoryLevel,
      });
    }
  }

  const landmarks = resolveTrackNodeLandmarks(graph, input.trackNodeAnnotations ?? []);
  if (landmarks.entryLandmarkId) {
    const entryCap =
      readPositiveCapacity(landmarks.entryFoupCapacity) ??
      inventoryFoupCapacity ??
      DEFAULT_GATE_FOUP_CAPACITY;
    loadPorts.push({
      loadPortId: SYNTHETIC_ENTRY_LOAD_PORT_ID,
      name: 'Entry Gate',
      landmarkId: landmarks.entryLandmarkId,
      siteRole: 'warehouse',
      trackNodeId: landmarks.entryLandmarkId,
      portKind: 'ENTRY_GATE',
      status: 'IDLE',
      foupCapacity: entryCap,
    });
  }
  if (landmarks.exitLandmarkId) {
    const exitCap =
      readPositiveCapacity(landmarks.exitFoupCapacity) ??
      inventoryFoupCapacity ??
      DEFAULT_GATE_FOUP_CAPACITY;
    loadPorts.push({
      loadPortId: SYNTHETIC_EXIT_LOAD_PORT_ID,
      name: 'Exit Gate',
      landmarkId: landmarks.exitLandmarkId,
      siteRole: 'shipping',
      trackNodeId: landmarks.exitLandmarkId,
      portKind: 'EXIT_GATE',
      status: 'IDLE',
      foupCapacity: exitCap,
    });
  }

  const processFlow = readProcessFlow(input.projectMetadata);
  const processStepByType = new Map(
    processFlow.steps.map((step) => [String(step.processType).trim().toUpperCase(), step])
  );
  const { processSequence, numberOfCycles } = buildAmhsProcessSequence(
    input.projectMetadata,
    tools.map((tool) => tool.toolType)
  );
  const outerLoopStockers = stockerSources.map((source) => ({
    id: stockerId(source),
    name: stockerName(source),
    processTypes: stockerExplicitProcessTypes(source),
    outside: isOutsideStocker(source),
  }));
  const outerLoopStockerIds = resolveOuterLoopStockerIds(
    outerLoopStockers,
    processSequence,
    processFlow.steps
  );
  const interbayDedicatedPath = buildOuterLoopPath(outerLoopStockerIds);

  const ohtElements = layoutElements.filter(
    (element) => element.type === 'oht-vehicle' && element.metadata.visible
  );
  const ohts: AmhsOht[] = [];
  for (const element of ohtElements) {
    const currentNodeId = nearestNodeId(graph, elementAnchor(element));
    if (!currentNodeId) continue;
    const modeRaw = String(element.customProperties?.ohtMode ?? '')
      .trim()
      .toLowerCase();
    const ohtMode =
      modeRaw === 'shared'
        ? ('shared' as const)
        : modeRaw === 'dedicated'
          ? ('dedicated' as const)
          : undefined;
    const cruiseSpeed = finitePositive(
      element.customProperties?.cruiseSpeedOverride,
      AMHS_PHYSICS_DEFAULTS.ohtCruiseSpeed
    );
    const baseOht: AmhsOht = {
      ohtId: element.id,
      name: element.metadata.name,
      status: 'Available',
      cruiseSpeed,
      homeNodeId: currentNodeId,
      currentNodeId,
      lengthM: AMHS_PHYSICS_DEFAULTS.ohtLengthM,
      minSeparationM: AMHS_PHYSICS_DEFAULTS.ohtMinSeparationM,
    };

    // Shared OHTs are a minimal fallback fleet — no corridor / role filters.
    if (ohtMode === 'shared') {
      ohts.push({ ...baseOht, ohtMode: 'shared' });
      continue;
    }

    const dedicatedFrom = semanticDedicatedEndpoint(
      String(element.customProperties?.dedicatedFrom ?? '').trim()
    );
    const dedicatedTo = semanticDedicatedEndpoint(
      String(element.customProperties?.dedicatedTo ?? '').trim()
    );
    const derivedRole = deriveOhtRoleFromEndpoints(dedicatedFrom, dedicatedTo);
    const derivedToolType = deriveToolTypeFromEndpoints(dedicatedFrom, dedicatedTo);
    const savedRole = parseOhtRole(element.customProperties?.ohtRole);
    // Legacy layouts store From/To; prefer derived role so interbay corridors aren't stuck as group.
    const ohtRole =
      dedicatedFrom && dedicatedTo ? (derivedRole ?? savedRole) : (savedRole ?? derivedRole);
    const fromEndpoint = parseDedicatedEndpoint(dedicatedFrom);
    const toEndpoint = parseDedicatedEndpoint(dedicatedTo);
    const hasLegacyCorridor = Boolean(dedicatedFrom && dedicatedTo);
    const legacyToolType =
      hasLegacyCorridor && ohtRole === 'interbay'
        ? fromEndpoint?.toolType
        : hasLegacyCorridor && ohtRole === 'entry'
          ? toEndpoint?.toolType
          : hasLegacyCorridor && ohtRole === 'exit'
            ? fromEndpoint?.toolType
            : undefined;
    const toolType = String(
      legacyToolType ?? element.customProperties?.toolType ?? derivedToolType ?? ''
    )
      .trim()
      .toUpperCase();
    const toToolType = String(
      (ohtRole === 'interbay' && hasLegacyCorridor ? toEndpoint?.toolType : undefined) ??
        element.customProperties?.toToolType ??
        ''
    )
      .trim()
      .toUpperCase();
    const allowedRaw = element.customProperties?.allowedRoles;
    const allowedRoles = Array.isArray(allowedRaw)
      ? allowedRaw.map((r) => parseOhtRole(r)).filter((r): r is NonNullable<typeof r> => Boolean(r))
      : undefined;
    const dedicatedPath =
      ohtRole === 'group'
        ? buildBayPath(
            stockerIdForToolType(stockerLookup, toolType, fallbackStockerId),
            toolType,
            tools,
            processStepByType.get(toolType)
          )
        : ohtRole
          ? mapDedicatedPathToStockerIds(
              buildDedicatedPath(ohtRole, toolType || undefined, toToolType || undefined),
              stockerLookup,
              fallbackStockerId
            )
          : undefined;
    // Sample payload only attaches toolType on process-bay (group) OHTs.
    const exportToolType = ohtRole === 'group' ? toolType : '';

    const oht: AmhsOht = { ...baseOht };
    if (ohtMode) oht.ohtMode = ohtMode;
    if (ohtRole) oht.ohtRole = ohtRole;
    if (exportToolType) oht.toolType = exportToolType;
    if (ohtRole === 'outerloop') oht.fleet = 'interbay';
    if (ohtRole === 'group') {
      oht.fleet = 'intrabay';
      oht.waitAtPickupForInventory = true;
    }
    if (dedicatedPath && dedicatedPath.length > 0) oht.dedicatedPath = dedicatedPath;
    if (allowedRoles && allowedRoles.length > 0) oht.allowedRoles = allowedRoles;
    ohts.push(oht);
  }

  const issues: string[] = [];
  const exportableEdges = getExportableTrackEdges(graph, layoutElements);
  if (graph.nodes.length === 0 || exportableEdges.length === 0) {
    issues.push('add at least one rail');
  }
  if (stockers.length === 0) issues.push('add at least one stocker');
  if (tools.length === 0) issues.push('add at least one equipment/tool');
  if (ohtElements.length === 0) issues.push('add at least one OHT');
  if (canvasLoadPortCount === 0) issues.push('add at least one load port');
  if (issues.length > 0) throw new AmhsRequestValidationError(issues);

  const holdingNodeIds = new Set([
    ...loadPorts.map((port) => port.trackNodeId),
    ...ohts.map((oht) => oht.currentNodeId),
  ]);
  const segments = exportableEdges.map(({ edge, segmentId }) => {
    const kind =
      edge.pathKind === 'connector'
        ? ('connector' as const)
        : edge.pathKind === 'turntable'
          ? ('turntable' as const)
          : undefined;
    return {
      segmentId,
      from: simulationTrackNodeId(edge.a),
      to: simulationTrackNodeId(edge.b),
      length: edge.length,
      direction: edge.isUnidirectional ? ('UNIDIRECTIONAL' as const) : ('BIDIRECTIONAL' as const),
      speed: AMHS_PHYSICS_DEFAULTS.trackSpeed,
      status: 'Available' as const,
      capacity: AMHS_PHYSICS_DEFAULTS.trackCapacity,
      costWeight: 1,
      preferred: false,
      forbidden: false,
      minHeadwaySec: AMHS_PHYSICS_DEFAULTS.minHeadwaySec,
      ...(kind ? { kind } : {}),
    };
  });
  const annotations = input.trackNodeAnnotations ?? [];
  const annotatedJunctionIndexes = new Set<number>();
  for (const annotation of annotations) {
    if (annotation.role !== 'junction' && annotation.isJunction !== true) continue;
    let bestIndex = -1;
    let bestDist2 = Infinity;
    const tol2 = ENDPOINT_TOLERANCE * ENDPOINT_TOLERANCE;
    graph.nodes.forEach((node, index) => {
      const dx = node.x - annotation.x;
      const dy = node.y - annotation.y;
      const dist2 = dx * dx + dy * dy;
      if (dist2 <= tol2 && dist2 < bestDist2) {
        bestIndex = index;
        bestDist2 = dist2;
      }
    });
    if (bestIndex >= 0) annotatedJunctionIndexes.add(bestIndex);
  }

  const connectedTracksByNode = graph.nodes.map((_, index) =>
    exportableEdges
      .filter(({ edge }) => edge.a === index || edge.b === index)
      .map(({ segmentId }) => segmentId)
  );

  const layoutJunction = layoutElements.find(
    (el) => el.type === 'cross-rail' || isJunctionRailType(resolveRailType(el))
  );
  const sharedJunctionWaitSec =
    typeof input.junctionWaitSec === 'number' && input.junctionWaitSec >= 0
      ? input.junctionWaitSec
      : layoutJunction
        ? getJunctionWaitSec(layoutJunction)
        : DEFAULT_JUNCTION_REQUEST_WAIT_SEC;

  const junctions = graph.nodes.flatMap((_, index) => {
    const connectedTracks = [...new Set(connectedTracksByNode[index])];
    const isAnnotated = annotatedJunctionIndexes.has(index);
    // Auto junctions at branch points (deg≥3); annotated J markers also when deg≥2.
    if (connectedTracks.length < 3 && !(isAnnotated && connectedTracks.length >= 2)) {
      return [];
    }
    const id = simulationTrackNodeId(index);
    const waitSec = sharedJunctionWaitSec;
    return [
      {
        junctionId: id,
        nodeId: id,
        connectedTracks,
        mutexGroup: id,
        waitSec,
      },
    ];
  });

  // H-connectors → track-network switches (throw states + home/far topology).
  const elementsById = new Map(layoutElements.map((el) => [el.id, el]));
  const switches: AmhsSwitch[] = exportableEdges.flatMap(({ edge, segmentId }) => {
    if (edge.pathKind !== 'connector') return [];
    const el = elementsById.get(edge.id);
    if (!el || !isConnectorElement(el)) return [];

    const homeNodeId = simulationTrackNodeId(edge.a);
    const farNodeId = simulationTrackNodeId(edge.b);
    const tracksAt = (nodeIndex: number) =>
      exportableEdges
        .filter(({ edge: e }) => e.a === nodeIndex || e.b === nodeIndex)
        .map(({ segmentId: id }) => id);
    const homeConnectedTracks = tracksAt(edge.a);
    const farConnectedTracks = tracksAt(edge.b);
    const connectedTracks = [...new Set([...homeConnectedTracks, ...farConnectedTracks])];
    const throws: AmhsSwitchThrow[] = [];
    for (const host of homeConnectedTracks.filter((id) => id !== segmentId)) {
      throws.push(
        {
          throw: 'LEFT',
          label: 'Enter connector from home rail',
          fromSegmentId: host,
          toSegmentId: segmentId,
        },
        {
          throw: 'LEFT',
          label: 'Exit connector to home rail',
          fromSegmentId: segmentId,
          toSegmentId: host,
        }
      );
    }
    for (const host of farConnectedTracks.filter((id) => id !== segmentId)) {
      throws.push(
        {
          throw: 'RIGHT',
          label: 'Enter connector from far rail',
          fromSegmentId: host,
          toSegmentId: segmentId,
        },
        {
          throw: 'RIGHT',
          label: 'Exit connector to far rail',
          fromSegmentId: segmentId,
          toSegmentId: host,
        }
      );
    }

    return [
      {
        switchId: el.id,
        nodeId: homeNodeId,
        name: el.metadata.name || el.id,
        defaultThrow: 'STRAIGHT',
        defaultState: 'STRAIGHT',
        states: ['STRAIGHT', 'LEFT', 'RIGHT'],
        throwTimeSec: 1.0,
        throws,
        segmentId,
        homeNodeId,
        farNodeId,
        homeConnectedTracks,
        farConnectedTracks,
        connectedTracks,
        mutexGroup: el.id,
        clearanceSec: 1,
        shiftState: getConnectorShiftState(el),
        shiftOffsetMeters: getConnectorShiftOffsetMeters(el),
        status: 'OPEN' as const,
        direction: 'BIDIRECTIONAL' as const,
      },
    ];
  });

  const foupsFromMeta = (() => {
    const flow =
      input.projectMetadata &&
      typeof input.projectMetadata.transportFlow === 'object' &&
      input.projectMetadata.transportFlow
        ? (input.projectMetadata.transportFlow as Record<string, unknown>)
        : null;
    const n = Number(flow?.foupCount);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  })();
  let foups = buildDefaultFoups(foupsFromMeta);
  const transportFlow = buildTransportFlowFromLayout({
    stockers,
    tools,
    foupCount: foups.foups.length,
    ohtCount: ohts.length,
    entryLandmarkId: landmarks.entryLandmarkId,
    exitLandmarkId: landmarks.exitLandmarkId,
    layoutMetadata: input.projectMetadata,
  });

  // Keep foups[].arrivalTimeSec aligned with transportFlow when using gap/serial/auto/segment-free.
  // Otherwise the UI default (index*5) fights foupGapSec / auto calculation.
  if (transportFlow.foupReleaseMode === 'gap') {
    const gap = Math.max(0, Number(transportFlow.foupGapSec) || 60);
    foups = {
      foups: foups.foups.map((foup, index) => ({
        ...foup,
        arrivalTimeSec: index * gap,
      })),
    };
  } else if (transportFlow.foupReleaseMode === 'serial') {
    // Serial ignores staggered arrivals — FOUP N+1 waits for FOUP N chain end.
    foups = {
      foups: foups.foups.map((foup) => ({
        ...foup,
        arrivalTimeSec: 0,
      })),
    };
  } else if (
    transportFlow.foupReleaseMode === 'auto' ||
    transportFlow.foupReleaseMode === 'segment-free'
  ) {
    // Let the API calculate release times; clear UI stagger defaults.
    foups = {
      foups: foups.foups.map(({ arrivalTimeSec, ...foup }) => {
        void arrivalTimeSec;
        return foup;
      }),
    };
  }

  return {
    loadPorts: { loadPorts },
    tools: { tools },
    stockers: { stockers },
    ohts: {
      ...(ohts.some((oht) => oht.ohtRole === 'outerloop') ? { interbayDedicatedPath } : {}),
      ohts,
    },
    foups,
    handling: {
      dropToPickSec: (() => {
        const handling = input.projectMetadata?.handling;
        if (!handling || typeof handling !== 'object') return DEFAULT_DROP_TO_PICK_SEC;
        const value = Number((handling as Record<string, unknown>).dropToPickSec);
        return Number.isFinite(value) && value >= 0 ? value : DEFAULT_DROP_TO_PICK_SEC;
      })(),
    },
    ...(transportFlow.foupReleaseMode != null
      ? { foupReleaseMode: transportFlow.foupReleaseMode }
      : {}),
    ...(includeTransportFlow ? { transportFlow } : {}),
    processSequence,
    numberOfCycles,
    trackNetwork: {
      fabId: input.floorPlanId || input.floorPlanName || 'current-layout',
      trackNetwork: {
        nodes: graph.nodes.map((node, index) => ({
          nodeId: simulationTrackNodeId(index),
          x: node.x,
          y: node.y,
          capacity: AMHS_PHYSICS_DEFAULTS.nodeCapacity,
          isHoldingPoint: holdingNodeIds.has(simulationTrackNodeId(index)),
        })),
        segments,
        junctions,
        switches,
      },
    },
    ...AMHS_SIMULATION_CONFIG,
    includeTransportFlow,
    // Fixed sample inventory block (stockerColumns Raw/Processed).
    inventory: AMHS_RUN_INVENTORY,
  };
}
